import {ReactNode, useState} from "react";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DropdownAction, DropdownActionItem, DropdownItem} from "@/components/quickActionsBar/DropdownAction.tsx";
import {QuickActionsBar} from "@/components/quickActionsBar/QuickActionsBar.tsx";
import {AccountUser} from "@/lib/types/accounts.ts";
import {EditUserModalAction} from "@/pages/accounts/edit/EditUserModalAction.tsx";
import {UserActionDelete} from "@/pages/accounts/list/actions/UserActionDelete.tsx";
import {UserActionMakePrimary} from "@/pages/accounts/list/actions/UserActionMakePrimary.tsx";
import {UserActionResendUserInvite} from "@/pages/accounts/list/actions/UserActionResendInvite.tsx";
import {UserActionReset2FA} from "@/pages/accounts/list/actions/UserActionReset2FA.tsx";
import {UserActionToggleActive} from "@/pages/accounts/list/actions/UserActionToggleActive.tsx";

type ActionsProps = {
  user: AccountUser;
};

export const UserActions = ({user}: ActionsProps) => {
  const {user: currentUser} = useCurrentUserContext();

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showReset2FADialog, setShowReset2FADialog] = useState(false);
  const [showResendInviteDialog, setShowResendInviteDialog] = useState(false);
  const [showToggleActiveDialog, setShowToggleActiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMakePrimaryDialog, setShowMakePrimaryDialog] = useState(false);

  const actions: ReactNode[] = [];
  const moreActions: DropdownItem[] = [];

  const canBecomePrimary = !user.is_primary && user.assigned_subaccounts.length === 0;

  if (currentUser!.has_admin_access) {
    moreActions.push(<DropdownActionItem text="Edit User" onClick={() => setShowEditDialog(true)} />);

    if (user.has_two_factor_device)
      moreActions.push(<DropdownActionItem text="Reset 2FA" onClick={() => setShowReset2FADialog(true)} />);

    if (!user.last_login)
      moreActions.push(<DropdownActionItem text="Resend Invite" onClick={() => setShowResendInviteDialog(true)} />);

    if (!user.is_primary)
      moreActions.push(
        <DropdownActionItem
          text={!user.is_active ? "Activate" : "Deactivate"}
          onClick={() => setShowToggleActiveDialog(true)}
        />
      );

    if (!user.is_primary)
      moreActions.push(<DropdownActionItem text="Delete User" onClick={() => setShowDeleteDialog(true)} />);

    if (currentUser?.is_primary && canBecomePrimary)
      moreActions.push(<DropdownActionItem text="Make an Owner" onClick={() => setShowMakePrimaryDialog(true)} />);

    actions.push(<DropdownAction id={`user-${user.id}`} items={moreActions} />);
  }

  return (
    <>
      <QuickActionsBar id={`user-action-${user.id}`} actions={actions} />

      <EditUserModalAction
        formId={`form-contact-${user.id}`}
        user={user}
        show={showEditDialog}
        setShow={setShowEditDialog}
      />
      <UserActionReset2FA user={user} show={showReset2FADialog} setShow={setShowReset2FADialog} />
      <UserActionResendUserInvite user={user} show={showResendInviteDialog} setShow={setShowResendInviteDialog} />
      <UserActionToggleActive user={user} show={showToggleActiveDialog} setShow={setShowToggleActiveDialog} />
      <UserActionDelete user={user} show={showDeleteDialog} setShow={setShowDeleteDialog} />
      <UserActionMakePrimary user={user} show={showMakePrimaryDialog} setShow={setShowMakePrimaryDialog} />
    </>
  );
};
